import { useHandler } from "@redotech/react-util/hook";
import { RedoReturnDetailsRpcClientContext } from "@redotech/redo-merchant-app-common/return-details-rpc-client-provider";
import type { RedoReturnDetailsRpcClient } from "@redotech/redo-merchant-return-details-rpc/client";
import { queryOptions, useQueries } from "@tanstack/react-query";
import {
  createContext,
  memo,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { progressForTrackedRun } from "./tracked-run-progress";

const POLL_INTERVAL_MS = 2000;
/** A run still unsettled after this long drops its card, so no tab polls a stuck return forever. */
const UNSETTLED_RUN_LIFETIME_MS = 90_000;

export type ProcessingProgress = Awaited<
  ReturnType<RedoReturnDetailsRpcClient["getReturnProcessingProgress"]>
>;

/** A return whose processing this session started. */
export interface TrackedProcessingRun {
  returnId: string;
  startedAt: number;
  /** The card was closed; the run still polls until it settles so pages can follow it. */
  dismissed: boolean;
  /** The last poll's answer; undefined until the first one lands. */
  progress: ProcessingProgress | undefined;
}

interface ProcessingRuns {
  runs: readonly TrackedProcessingRun[];
  /** Start showing a card for this return; a second call for the same return restarts it. */
  track(returnId: string): void;
  dismiss(returnId: string): void;
}

export const ProcessingRunsContext = createContext<ProcessingRuns | undefined>(
  undefined,
);
ProcessingRunsContext.displayName = "ProcessingRunsContext";

export function isProcessingRunSettled(
  progress: ProcessingProgress | undefined,
): boolean {
  return progress?.state === "done" || progress?.state === "failed";
}

/**
 * Lives above the router so a run's card survives leaving the return page.
 * Each run polls the server until it settles, like the bulk action cards.
 */
export const ProcessingRunsProvider = memo(function ProcessingRunsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const rpc = useContext(RedoReturnDetailsRpcClientContext);
  const [tracked, setTracked] = useState<
    { returnId: string; startedAt: number; dismissed: boolean }[]
  >([]);
  // A run seen running that drops back to "waiting" stopped without
  // finishing, which the return itself does not record.
  const sawRunning = useRef(new Set<string>());

  const results = useQueries({
    queries: tracked.map((run) =>
      queryOptions({
        queryKey: ["return-processing-progress", run.returnId, run.startedAt],
        queryFn: async (): Promise<ProcessingProgress> => {
          if (!rpc) {
            throw new Error("Return details RPC client is unavailable");
          }
          const progress = await rpc.getReturnProcessingProgress({
            returnId: run.returnId,
          });
          if (progress.state === "running") {
            sawRunning.current.add(run.returnId);
          }
          return progressForTrackedRun(
            progress,
            sawRunning.current.has(run.returnId),
          );
        },
        enabled: !!rpc,
        refetchInterval: (query) =>
          isProcessingRunSettled(query.state.data) ? false : POLL_INTERVAL_MS,
        refetchOnWindowFocus: false,
        retry: false,
      }),
    ),
  });

  const track = useHandler((returnId: string) => {
    sawRunning.current.delete(returnId);
    setTracked((previous) => [
      ...previous.filter((run) => run.returnId !== returnId),
      { returnId, startedAt: Date.now(), dismissed: false },
    ]);
  });
  const dismiss = useHandler((returnId: string) => {
    setTracked((previous) =>
      previous.map((run) =>
        run.returnId === returnId ? { ...run, dismissed: true } : run,
      ),
    );
  });

  const runs = useMemo(
    () =>
      tracked.map((run, index) => ({ ...run, progress: results[index]?.data })),
    [tracked, results],
  );

  // A dismissed run is kept only while unsettled.
  const settledDismissedIds = runs
    .filter((run) => run.dismissed && isProcessingRunSettled(run.progress))
    .map((run) => run.returnId)
    .join(",");
  useEffect(() => {
    if (!settledDismissedIds) {
      return;
    }
    const finished = new Set(settledDismissedIds.split(","));
    setTracked((previous) =>
      previous.filter((run) => !finished.has(run.returnId)),
    );
  }, [settledDismissedIds]);

  const runsRef = useRef(runs);
  runsRef.current = runs;
  const oldestUnsettledStart = Math.min(
    ...runs
      .filter((run) => !isProcessingRunSettled(run.progress))
      .map((run) => run.startedAt),
  );
  useEffect(() => {
    if (!Number.isFinite(oldestUnsettledStart)) {
      return;
    }
    const expireAt = oldestUnsettledStart + UNSETTLED_RUN_LIFETIME_MS;
    const timeout = setTimeout(
      () => {
        const expired = new Set(
          runsRef.current
            .filter(
              (run) =>
                !isProcessingRunSettled(run.progress) &&
                run.startedAt <= Date.now() - UNSETTLED_RUN_LIFETIME_MS,
            )
            .map((run) => run.returnId),
        );
        if (expired.size > 0) {
          setTracked((previous) =>
            previous.filter((run) => !expired.has(run.returnId)),
          );
        }
      },
      Math.max(0, expireAt - Date.now()),
    );
    return () => clearTimeout(timeout);
  }, [oldestUnsettledStart]);

  const value = useMemo(
    () => ({ runs, track, dismiss }),
    [runs, track, dismiss],
  );

  return (
    <ProcessingRunsContext.Provider value={value}>
      {children}
    </ProcessingRunsContext.Provider>
  );
});
